import { useRef } from 'react';
import PropTypes from 'prop-types';

const FILE_ICONS = {
  'image/': '🖼️',
  'application/pdf': '📄',
  'application/msword': '📝',
  'application/vnd.openxmlformats-officedocument.wordprocessingml': '📝',
  'application/vnd.ms-excel': '📊',
  'application/vnd.openxmlformats-officedocument.spreadsheetml': '📊',
  'application/vnd.ms-powerpoint': '📽️',
  'application/vnd.openxmlformats-officedocument.presentationml': '📽️',
  'text/': '📃',
  'application/zip': '📦',
  'application/x-rar': '📦',
  'application/x-7z': '📦',
  default: '📎',
};

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/json',
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;

function getFileIcon(mimeType) {
  for (const [key, icon] of Object.entries(FILE_ICONS)) {
    if (key !== 'default' && mimeType?.startsWith(key)) {
      return icon;
    }
  }
  return FILE_ICONS.default;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileAttachment({
  files = [],
  existingAttachments = [],
  onChange,
  onRemoveExisting,
  disabled = false,
  maxFiles = MAX_FILES,
  compact = false,
}) {
  const inputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];
    const errors = [];

    selectedFiles.forEach((file) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" - Tipo de archivo no permitido`);
      } else if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" - Excede el tamaño máximo (10MB)`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      alert('Algunos archivos no se pudieron agregar:\n\n' + errors.join('\n'));
    }

    const totalFiles = files.length + existingAttachments.length + validFiles.length;
    if (totalFiles > maxFiles) {
      alert(`Máximo ${maxFiles} archivos permitidos`);
      const allowedCount = maxFiles - files.length - existingAttachments.length;
      onChange([...files, ...validFiles.slice(0, Math.max(0, allowedCount))]);
    } else {
      onChange([...files, ...validFiles]);
    }

    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const totalFiles = files.length + existingAttachments.length;

  // Estilos adaptados para modo compact
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: compact ? '8px' : '12px',
  };

  const dropzoneStyle = {
    border: `1px dashed ${compact ? '#d1d5db' : '#e2e8f0'}`,
    borderRadius: compact ? '8px' : '12px',
    padding: compact ? '12px 16px' : '20px',
    textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    backgroundColor: disabled ? '#f9fafb' : '#ffffff',
    transition: 'all 0.15s ease',
    opacity: disabled ? 0.6 : 1,
    display: compact ? 'flex' : 'block',
    alignItems: compact ? 'center' : 'stretch',
    justifyContent: compact ? 'center' : 'stretch',
    gap: compact ? '10px' : '0',
  };

  const fileListStyle = {
    display: 'flex',
    flexDirection: compact ? 'row' : 'column',
    flexWrap: compact ? 'wrap' : 'nowrap',
    gap: compact ? '6px' : '8px',
  };

  const fileItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: compact ? '6px' : '10px',
    padding: compact ? '6px 10px' : '10px 12px',
    backgroundColor: '#f9fafb',
    borderRadius: compact ? '6px' : '8px',
    border: '1px solid #e5e7eb',
    flex: compact ? '0 0 auto' : 'none',
  };

  const fileInfoStyle = {
    flex: 1,
    minWidth: 0,
    display: compact ? 'flex' : 'block',
    alignItems: compact ? 'center' : 'stretch',
    gap: compact ? '6px' : '0',
  };

  const fileNameStyle = {
    fontSize: compact ? '12px' : '13px',
    fontWeight: '500',
    color: '#1f2937',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: compact ? '120px' : 'none',
  };

  const fileSizeStyle = {
    fontSize: compact ? '10px' : '11px',
    color: '#9ca3af',
  };

  const removeButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: compact ? '18px' : '24px',
    height: compact ? '18px' : '24px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    color: '#9ca3af',
    cursor: 'pointer',
    fontSize: compact ? '14px' : '16px',
    transition: 'all 0.15s ease',
    flexShrink: 0,
  };

  return (
    <div style={containerStyle}>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* Existing Attachments - mostrar primero */}
      {existingAttachments.length > 0 && (
        <div style={fileListStyle}>
          {existingAttachments.map((attachment) => (
            <div key={attachment.id} style={fileItemStyle}>
              <span style={{ fontSize: compact ? '14px' : '20px' }}>{getFileIcon(attachment.fileType)}</span>
              <div style={fileInfoStyle}>
                <a
                  href={attachment.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ ...fileNameStyle, color: '#6366f1', textDecoration: 'none' }}
                  title={attachment.filename}
                >
                  {attachment.filename}
                </a>
                <span style={fileSizeStyle}>{formatFileSize(attachment.fileSize)}</span>
              </div>
              {!disabled && onRemoveExisting && (
                <button
                  type="button"
                  style={removeButtonStyle}
                  onClick={() => onRemoveExisting(attachment.id)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#fee2e2';
                    e.target.style.color = '#ef4444';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#9ca3af';
                  }}
                  title="Eliminar"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New Files */}
      {files.length > 0 && (
        <div style={fileListStyle}>
          {!compact && <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>Nuevos:</span>}
          {files.map((file, index) => (
            <div key={index} style={{ ...fileItemStyle, backgroundColor: '#ecfdf5', borderColor: '#a7f3d0' }}>
              <span style={{ fontSize: compact ? '14px' : '20px' }}>{getFileIcon(file.type)}</span>
              <div style={fileInfoStyle}>
                <span style={fileNameStyle} title={file.name}>{file.name}</span>
                <span style={fileSizeStyle}>{formatFileSize(file.size)}</span>
              </div>
              {!disabled && (
                <button
                  type="button"
                  style={removeButtonStyle}
                  onClick={() => handleRemoveFile(index)}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#fee2e2';
                    e.target.style.color = '#ef4444';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#9ca3af';
                  }}
                  title="Eliminar"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Dropzone - más compacto y al final */}
      {totalFiles < maxFiles && (
        <div
          style={dropzoneStyle}
          onClick={() => !disabled && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) {
              e.currentTarget.style.borderColor = '#6366f1';
              e.currentTarget.style.backgroundColor = '#eef2ff';
            }
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.borderColor = compact ? '#d1d5db' : '#e2e8f0';
            e.currentTarget.style.backgroundColor = disabled ? '#f9fafb' : '#ffffff';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = compact ? '#d1d5db' : '#e2e8f0';
            e.currentTarget.style.backgroundColor = disabled ? '#f9fafb' : '#ffffff';
            if (!disabled && e.dataTransfer.files.length > 0) {
              const event = { target: { files: e.dataTransfer.files, value: '' } };
              handleFileSelect(event);
            }
          }}
        >
          {compact ? (
            <>
              <span style={{ fontSize: '16px' }}>📎</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                Arrastra o <span style={{ color: '#6366f1', fontWeight: '500' }}>selecciona</span>
              </span>
            </>
          ) : (
            <>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📎</div>
              <p style={{ fontSize: '13px', color: '#4b5563', margin: 0 }}>
                Arrastra archivos aquí o <span style={{ color: '#6366f1', fontWeight: '500' }}>haz clic para seleccionar</span>
              </p>
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                Máx. {maxFiles} archivos, 10MB cada uno
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

FileAttachment.propTypes = {
  files: PropTypes.array,
  existingAttachments: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onRemoveExisting: PropTypes.func,
  disabled: PropTypes.bool,
  maxFiles: PropTypes.number,
  compact: PropTypes.bool,
};
